// Sprint Clockify.A — config + sync manual + mapping.
// Aba dentro de Admin > Integrações > Clockify.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Input, Form, Typography, Space, Flex, Alert, Tag, Select,
  App as AntApp, Table, Statistic, Spin, DatePicker,
} from 'antd';
import { LinkOutlined, SyncOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

export default function AdminIntegracaoClockify() {
  return <AntApp><Inner /></AntApp>;
}

function Inner() {
  const { message } = AntApp.useApp();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | undefined>();
  const [testando, setTestando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<any>(null);
  const [userMap, setUserMap] = useState<any[]>([]);
  const [projectMap, setProjectMap] = useState<any[]>([]);
  const [categoriaMap, setCategoriaMap] = useState<any[]>([]);
  const [tabSec, setTabSec] = useState<'config' | 'users' | 'projects' | 'categorias' | 'relatorio'>('config');

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_config_get' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setConfig(d || null);
      if (d?.workspace_id) setWorkspaceId(d.workspace_id);
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function testar() {
    if (!apiKey.trim()) { message.warning('Cole a API key primeiro'); return; }
    setTestando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_listar_workspaces', api_key: apiKey.trim() },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.error || d?.erro) {
        message.error(d.error || d.erro);
      } else {
        setWorkspaces(Array.isArray(d) ? d : []);
        message.success(`${d.length} workspace(s) encontrados`);
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setTestando(false); }
  }

  async function salvar() {
    if (!apiKey.trim() || !workspaceId) { message.warning('API key e workspace obrigatórios'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const ws = workspaces.find(w => w.id === workspaceId);
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_config_set',
          api_key: apiKey.trim(),
          workspace_id: workspaceId,
          workspace_nome: ws?.name || null },
        { headers: { Authorization: token } });
      message.success('Configurado.');
      setApiKey('');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function sincronizar() {
    setSincronizando(true); setResultadoSync(null);
    try {
      const token = await getToken();
      // Se há data de corte configurada → usa ela como início.
      // Caso contrário → fallback de 14 dias.
      const body: any = { action: 'clockify_sync_horas' };
      if (config?.data_inicio_rateio) {
        body.usar_data_corte = true;
      } else {
        body.dias = 14;
      }
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        body,
        { headers: { Authorization: token }, timeout: 180_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultadoSync(d);
      message.success(`Sync OK — ${d.horas_upsertadas} hora(s)`);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSincronizando(false); }
  }

  async function carregarUserMap() {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'clockify_listar_user_map' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setUserMap(Array.isArray(d) ? d : []);
  }

  async function carregarProjectMap() {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'clockify_listar_project_map' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setProjectMap(Array.isArray(d) ? d : []);
  }

  async function carregarCategoriaMap() {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'clockify_categoria_listar' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setCategoriaMap(Array.isArray(d?.projetos) ? d.projetos : (Array.isArray(d) ? d : []));
  }

  useEffect(() => {
    if (tabSec === 'users') carregarUserMap();
    if (tabSec === 'projects') carregarProjectMap();
    if (tabSec === 'categorias') carregarCategoriaMap();
  }, [tabSec]);

  if (loading) return <Spin />;

  const conectado = !!config?.api_key_mask;

  return (
    <div style={{ padding: '8px 4px' }}>
      <Flex gap={8} style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <Button size="small" type={tabSec === 'config' ? 'primary' : 'default'}
          onClick={() => setTabSec('config')}>Conexão</Button>
        <Button size="small" type={tabSec === 'users' ? 'primary' : 'default'}
          onClick={() => setTabSec('users')}>👤 Usuários ({userMap.length})</Button>
        <Button size="small" type={tabSec === 'projects' ? 'primary' : 'default'}
          onClick={() => setTabSec('projects')}>📁 Projetos ({projectMap.length || '—'})</Button>
        <Button size="small" type={tabSec === 'categorias' ? 'primary' : 'default'}
          onClick={() => setTabSec('categorias')}>🏷️ Categorias ({categoriaMap.length || '—'})</Button>
        <Button size="small" type={tabSec === 'relatorio' ? 'primary' : 'default'}
          onClick={() => setTabSec('relatorio')}>📨 Relatório diário</Button>
      </Flex>
      {tabSec === 'relatorio' && <SecaoRelatorioDiario />}

      {tabSec === 'config' && (
        <>
          <Card size="small" style={{ marginBottom: 14 }}>
            <Flex justify="space-between" align="center">
              <div>
                <Title level={5} style={{ margin: 0, color: '#020D7F' }}>⏱️ Clockify</Title>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Pull de horas (Modo A). Sync manual ou via cron diário.
                </Text>
              </div>
              {conectado
                ? <Tag color="success">● Conectado · {config?.workspace_nome || config?.workspace_id}</Tag>
                : <Tag color="error">○ Não configurado</Tag>}
            </Flex>

            {conectado && (
              <Flex gap={20} style={{ marginTop: 14 }}>
                <Statistic title="API Key" value={config.api_key_mask} valueStyle={{ fontSize: 13, fontFamily: 'monospace' }} />
                <Statistic title="Última sync" value={config.ultima_sync ? new Date(config.ultima_sync).toLocaleString('pt-BR') : 'Nunca'} valueStyle={{ fontSize: 13 }} />
                <Statistic title="Data de corte do rateio" value={config.data_inicio_rateio ? new Date(config.data_inicio_rateio).toLocaleDateString('pt-BR') : '— não definida —'} valueStyle={{ fontSize: 13, color: config.data_inicio_rateio ? '#0930D0' : '#B91626' }} />
              </Flex>
            )}
          </Card>

          {conectado && (
            <Card size="small" style={{ marginBottom: 14 }}
              title={<><CalendarOutlined /> Data de corte do rateio automático</>}>
              <Alert type="warning" showIcon style={{ marginBottom: 12, fontSize: 12 }}
                message="O rateio automático Clockify só aplica para competências A PARTIR desta data."
                description="Lançamentos anteriores foram rateados manualmente pela Marina no Conta Azul e ficam intocáveis. Não tente refazer o passado — congelado por design." />
              <DataInicioRateio config={config} onSaved={carregar} />
            </Card>
          )}

          {/* Configuração / re-configuração */}
          <Card size="small" title={conectado ? '🔄 Reconfigurar' : '⚙ Conectar Clockify'}>
            <Form layout="vertical" size="small">
              <Form.Item label="API Key (Clockify)"
                extra="Pegue em https://app.clockify.me/user/settings (seção API)">
                <Input.Password value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder="Cole sua API key pessoal" />
              </Form.Item>
              <Flex gap={8} style={{ marginBottom: 12 }}>
                <Button type="primary" ghost icon={<LinkOutlined />}
                  loading={testando} onClick={testar} disabled={!apiKey.trim()}>
                  Testar conexão (listar workspaces)
                </Button>
              </Flex>
              {workspaces.length > 0 && (
                <Form.Item label="Workspace" required>
                  <Select value={workspaceId} onChange={setWorkspaceId}
                    placeholder="Escolha o workspace"
                    options={workspaces.map(w => ({ label: `${w.name} (${w.id})`, value: w.id }))} />
                </Form.Item>
              )}
              <Button type="primary" onClick={salvar} loading={salvando}
                disabled={!apiKey.trim() || !workspaceId}>
                Salvar configuração
              </Button>
            </Form>
          </Card>

          {conectado && (
            <Card size="small" title="🔄 Sincronizar horas" style={{ marginTop: 14 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                {config?.data_inicio_rateio
                  ? <>Puxa time-entries <strong>desde {new Date(config.data_inicio_rateio).toLocaleDateString('pt-BR')}</strong> (data de corte) pra cache local + atualiza mappings de users/projects.</>
                  : <>Puxa time-entries dos últimos 14 dias (configure data de corte acima pra puxar desde lá).</>
                }
              </Text>
              <Button type="primary" icon={<SyncOutlined spin={sincronizando} />}
                onClick={sincronizar} loading={sincronizando}>
                Sincronizar agora
              </Button>
              {resultadoSync && (
                <Alert type="success" style={{ marginTop: 12 }} showIcon
                  message={`Sync OK`}
                  description={
                    <div style={{ fontSize: 11 }}>
                      {resultadoSync.janela && (
                        <div style={{ color: '#666', marginBottom: 4 }}>
                          📅 Janela: {resultadoSync.janela.modo}
                        </div>
                      )}
                      <div>👤 Users atualizados: {resultadoSync.users_atualizados}</div>
                      <div>📁 Projetos atualizados: {resultadoSync.projetos_atualizados}</div>
                      <div>⏱️ Horas upsertadas: {resultadoSync.horas_upsertadas}</div>
                      {resultadoSync.erros?.length > 0 && (
                        <div style={{ color: '#B07500', marginTop: 6 }}>
                          ⚠ {resultadoSync.erros.length} erro(s): {resultadoSync.erros.slice(0, 3).join(' · ')}
                        </div>
                      )}
                    </div>
                  } />
              )}
            </Card>
          )}
        </>
      )}

      {tabSec === 'users' && (
        <Card size="small" title="👤 Mapeamento de Usuários (Email ↔ Clockify User)">
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
            Atualizado automaticamente no sync. Email do Clockify usado como chave.
          </Text>
          <Table size="small" rowKey="email" dataSource={userMap}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: 'Email', dataIndex: 'email', key: 'e' },
              { title: 'Clockify User Name', dataIndex: 'clockify_user_name', key: 'n' },
              { title: 'Clockify User ID', dataIndex: 'clockify_user_id', key: 'id',
                render: v => <Text style={{ fontFamily: 'monospace', fontSize: 10 }}>{v}</Text> },
            ] as ColumnsType<any>}
          />
        </Card>
      )}

      {tabSec === 'projects' && (
        <ProjetosMapping recarregar={carregarProjectMap} projectMap={projectMap} />
      )}

      {tabSec === 'categorias' && (
        <CategoriasMapping recarregar={carregarCategoriaMap} categoriaMap={categoriaMap} />
      )}
    </div>
  );
}

// ─── Mapping inline de projetos Clockify ↔ Contratos ───────────────────
function ProjetosMapping({ recarregar, projectMap }: {
  recarregar: () => Promise<void>; projectMap: any[];
}) {
  const { message } = AntApp.useApp();
  const [contratos, setContratos] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await getToken();
      // Só contratos PAI — aditivos não aparecem no dropdown porque no Clockify
      // não há representação de aditivo (tudo aponta pro contrato pai).
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_listar_contratos_pai' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setContratos(Array.isArray(d) ? d : []);
    })();
  }, []);

  async function vincular(clockifyId: string, clockifyNome: string, contratoId: string | null) {
    setSalvandoId(clockifyId);
    try {
      const token = await getToken();
      if (contratoId) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_atualizar_project_map',
            contrato_id: contratoId,
            clockify_project_id: clockifyId,
            clockify_project_nome: clockifyNome },
          { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_remover_project_map',
            clockify_project_id: clockifyId },
          { headers: { Authorization: token } });
      }
      message.success('OK');
      await recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvandoId(null); }
  }

  const filtrados = projectMap.filter(p => {
    if (!mostrarArquivados && p.archived) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (p.clockify_project_nome || '').toLowerCase().includes(q)
          || (p.cliente_nome || '').toLowerCase().includes(q)
          || (p.contrato_numero || '').toLowerCase().includes(q);
    }
    return true;
  });

  const vinculados = projectMap.filter(p => p.contrato_id).length;
  const naoVinculados = projectMap.filter(p => !p.contrato_id && !p.archived).length;

  return (
    <Card size="small" title={
      <Flex justify="space-between" align="center">
        <Text strong style={{ color: '#020D7F' }}>
          📁 Projetos Clockify ({projectMap.length}) — vincule a contratos
        </Text>
        <Space size={6}>
          <Tag color="success">{vinculados} vinculados</Tag>
          <Tag color="warning">{naoVinculados} pendentes</Tag>
        </Space>
      </Flex>
    }>
      <Alert type="info" showIcon style={{ marginBottom: 10, fontSize: 11 }}
        message="Aditivos não aparecem aqui — projetos Clockify mapeiam direto ao contrato pai. Múltiplos projetos Clockify podem apontar pro mesmo contrato (útil quando há aliases como 'P_25034' e 'P25034' criados separadamente no Clockify)." />
      <Flex gap={8} style={{ marginBottom: 10 }} align="center" wrap>
        <Input placeholder="Buscar nome do projeto, cliente, contrato…"
          value={busca} onChange={e => setBusca(e.target.value)}
          allowClear size="small" style={{ flex: 1, maxWidth: 400 }} />
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={mostrarArquivados}
            onChange={e => setMostrarArquivados(e.target.checked)} />
          mostrar arquivados
        </label>
        <AutoMapearBtn recarregar={recarregar} />
      </Flex>
      <Table size="small" rowKey="clockify_project_id" dataSource={filtrados}
        pagination={{ pageSize: 50 }}
        columns={[
          {
            title: 'Projeto Clockify', dataIndex: 'clockify_project_nome', key: 'cn',
            render: (v: string, r: any) => (
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>
                  {r.cor && <span style={{ display: 'inline-block', width: 8, height: 8,
                    background: r.cor, borderRadius: 2, marginRight: 6 }} />}
                  {v}
                  {r.archived && <Tag color="default" style={{ marginLeft: 6, fontSize: 9 }}>arquivado</Tag>}
                </div>
                {r.cliente_nome && (
                  <div style={{ fontSize: 10, color: '#888' }}>cliente Clockify: {r.cliente_nome}</div>
                )}
              </div>
            ),
          },
          {
            title: 'Horas registradas', dataIndex: 'horas_count', key: 'hc',
            align: 'center', width: 110,
            render: (v: number) => v > 0
              ? <Tag color="blue">{v}</Tag>
              : <Text type="secondary" style={{ fontSize: 10 }}>—</Text>,
          },
          {
            title: 'Contrato vinculado', key: 'vinc', width: 360,
            render: (_: any, r: any) => (
              <Select
                value={r.contrato_id || undefined}
                onChange={(v) => vincular(r.clockify_project_id, r.clockify_project_nome, v || null)}
                placeholder="— sem vínculo —"
                allowClear showSearch optionFilterProp="label"
                size="small" style={{ width: '100%' }}
                loading={salvandoId === r.clockify_project_id}
                options={contratos.map((c: any) => ({
                  label: `${c.numero} — ${c.titulo}${c.cliente_nome ? ` · ${c.cliente_nome}` : ''}`,
                  value: c.id,
                }))} />
            ),
          },
        ] as ColumnsType<any>}
      />
    </Card>
  );
}

// ─── Botão Auto-mapear projetos Clockify por número de contrato ─────────────
// Faz match nome Clockify "P_25014 - X" ou "P26008 - X" ↔ contratos.numero
function AutoMapearBtn({ recarregar }: { recarregar: () => Promise<void> }) {
  const { message, modal } = AntApp.useApp();
  const [carregando, setCarregando] = useState(false);

  async function abrir() {
    setCarregando(true);
    try {
      const token = await getToken();
      // Dry-run primeiro
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_auto_mapear_projetos', modo: 'dry-run' },
        { headers: { Authorization: token }, timeout: 60_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const stats = d?.stats || {};
      const propostos = (d?.resultados || []).filter((x: any) => x.status === 'proposto');
      const semNumero = stats.sem_numero || 0;
      const semContrato = stats.sem_contrato || 0;
      // número de contrato que existe, mas o nome do projeto é de outro cliente
      // (ou o número aparece em dois contratos): não liga sozinho — é para olhar
      const paraConferir = (stats.nome_diverge || 0) + (stats.numero_repetido || 0);
      // ligação que uma pessoa removeu: o robô (e este botão) não refaz
      const removidos = stats.removido_a_mao || 0;

      if (propostos.length === 0) {
        message.info(`Nenhum projeto pra mapear (${semNumero} sem número, ${semContrato} sem contrato`
          + (paraConferir ? `, ${paraConferir} para conferir o nome` : '')
          + (removidos ? `, ${removidos} com a ligação removida à mão` : '') + ')');
        return;
      }

      modal.confirm({
        title: `Auto-mapear ${propostos.length} projetos Clockify a contratos?`,
        width: 700,
        content: (
          <div>
            <div style={{ marginBottom: 10, fontSize: 12 }}>
              Match feito por número (P_25014, P26008, …) extraído do nome Clockify
              contra <code>operacao.contratos.numero</code>.
            </div>
            <Table size="small" pagination={{ pageSize: 8 }} rowKey="clockify_project_id"
              dataSource={propostos}
              columns={[
                { title: 'Clockify', dataIndex: 'clockify_nome', key: 'cn',
                  render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
                { title: 'Nº', dataIndex: 'numero_extraido', key: 'n', width: 70 },
                { title: 'Contrato', dataIndex: 'contrato_numero', key: 'c', width: 90 },
                { title: 'Título', dataIndex: 'contrato_titulo', key: 't',
                  render: (v: string) => <Text style={{ fontSize: 10, color: '#666' }} ellipsis>{v}</Text> },
              ] as ColumnsType<any>}
            />
            {(semNumero > 0 || semContrato > 0) && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#888' }}>
                Não-mapeados: {semNumero} sem número no nome, {semContrato} com número mas sem contrato correspondente
              </div>
            )}
            {paraConferir > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#ad6800' }}>
                {paraConferir} projeto(s) têm número de contrato, mas o nome é de outro cliente, não diz de quem é
                (só o tipo de serviço) ou o número se repete:
                não são ligados sozinhos. Ligue à mão na lista, ou corrija o nome no Clockify.
              </div>
            )}
            {removidos > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#ad6800' }}>
                {removidos} projeto(s) tiveram a ligação removida à mão e por isso não são religados sozinhos.
                Para ligar de novo, escolha o contrato na lista.
              </div>
            )}
          </div>
        ),
        okText: 'Aplicar mapeamento',
        cancelText: 'Cancelar',
        onOk: async () => {
          try {
            const token2 = await getToken();
            const r2 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
              { action: 'clockify_auto_mapear_projetos', modo: 'aplicar' },
              { headers: { Authorization: token2 }, timeout: 120_000 });
            const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
            message.success(`${d2?.stats?.aplicados || 0} projetos mapeados`);
            await recarregar();
          } catch (e: any) {
            message.error('Erro: ' + (e.response?.data?.error || e.message));
          }
        },
      });
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }

  return (
    <Button size="small" type="primary" ghost loading={carregando} onClick={abrir}>
      🪄 Auto-mapear por número
    </Button>
  );
}

// ─── Data de corte do rateio automático ─────────────────────────────────────
// Configura a data a partir da qual o rateio Clockify se aplica. Lançamentos
// anteriores foram rateados manualmente no CA e ficam intocados.
function DataInicioRateio({ config, onSaved }: { config: any; onSaved: () => Promise<void> }) {
  const { message } = AntApp.useApp();
  const [data, setData] = useState<any>(config?.data_inicio_rateio ? dayjs(config.data_inicio_rateio) : null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!data) { message.warning('Selecione uma data'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_config_set',
          apenas_data_inicio: true,
          data_inicio_rateio: data.format('YYYY-MM-DD') },
        { headers: { Authorization: token } });
      message.success('Data de corte salva');
      await onSaved();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Flex gap={12} align="center" wrap>
      <div>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Aplicar rateio Clockify a partir de:</div>
        <DatePicker
          value={data}
          onChange={setData}
          format="DD/MM/YYYY"
          placeholder="Selecione a data"
          style={{ width: 180 }}
        />
      </div>
      <Button type="primary" onClick={salvar} loading={salvando} disabled={!data}>
        Salvar
      </Button>
      {config?.data_inicio_rateio && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          Atual: <strong>{new Date(config.data_inicio_rateio).toLocaleDateString('pt-BR')}</strong>
        </Text>
      )}
    </Flex>
  );
}

// ─── Mapping de projetos Clockify NÃO-projeto → categoria DRE ──────────────
// Usado pelo Rateio Mensal F1: projetos como "Administração", "Comercial Interno"
// caem em categorias DRE específicas (Mão de obra administrativa, Comercial, etc).
function CategoriasMapping({ recarregar, categoriaMap }: {
  recarregar: () => Promise<void>; categoriaMap: any[];
}) {
  const { message } = AntApp.useApp();
  const [categoriasDre, setCategoriasDre] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_categorias_dre_listar' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCategoriasDre(Array.isArray(d?.categorias) ? d.categorias : []);
    })();
  }, []);

  async function vincular(clockifyId: string, clockifyNome: string, categoria: string | null) {
    setSalvandoId(clockifyId);
    try {
      const token = await getToken();
      if (categoria) {
        const cat = categoriasDre.find(c => c.categoria === categoria);
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_categoria_atualizar',
            clockify_project_id: clockifyId,
            clockify_project_nome: clockifyNome,
            categoria_dre: categoria,
            grupo_dre: cat?.grupo_dre || null },
          { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_categoria_remover',
            clockify_project_id: clockifyId },
          { headers: { Authorization: token } });
      }
      message.success('OK');
      await recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvandoId(null); }
  }

  const filtrados = categoriaMap.filter(p => {
    if (!mostrarArquivados && p.archived) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (p.clockify_project_nome || '').toLowerCase().includes(q)
          || (p.categoria_dre || '').toLowerCase().includes(q);
    }
    return true;
  });

  const vinculados = categoriaMap.filter(p => p.categoria_dre).length;
  const naoVinculados = categoriaMap.filter(p => !p.categoria_dre && !p.archived).length;

  return (
    <Card size="small" title={
      <Flex justify="space-between" align="center">
        <Text strong style={{ color: '#020D7F' }}>
          🏷️ CCs Não-Projeto ({categoriaMap.length}) — vincule a categorias DRE
        </Text>
        <Space size={6}>
          <Tag color="success">{vinculados} vinculados</Tag>
          <Tag color="warning">{naoVinculados} pendentes</Tag>
        </Space>
      </Flex>
    }>
      <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 11 }}
        message="Apenas CCs textuais (Administração, Comercial Interno, Treinamento, etc.) aparecem aqui — projetos com padrão de número (P_NNNNN / PNNNNN) e projetos já vinculados a contratos são filtrados automaticamente (eles vivem na aba Projetos). As horas em CCs não-projeto viram custo indireto na categoria DRE escolhida. Default sem mapeamento: 'Mão de obra administrativa'." />

      <Flex gap={8} style={{ marginBottom: 10 }}>
        <Input placeholder="Buscar nome do projeto ou categoria…"
          value={busca} onChange={e => setBusca(e.target.value)}
          allowClear size="small" style={{ flex: 1, maxWidth: 400 }} />
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={mostrarArquivados}
            onChange={e => setMostrarArquivados(e.target.checked)} />
          mostrar arquivados
        </label>
      </Flex>
      <Table size="small" rowKey="clockify_project_id" dataSource={filtrados}
        pagination={{ pageSize: 50 }}
        columns={[
          {
            title: 'Projeto Clockify', dataIndex: 'clockify_project_nome', key: 'cn',
            render: (v: string, r: any) => (
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>
                  {v}
                  {r.archived && <Tag color="default" style={{ marginLeft: 6, fontSize: 9 }}>arquivado</Tag>}
                </div>
                {r.cliente_nome && (
                  <div style={{ fontSize: 10, color: '#888' }}>cliente Clockify: {r.cliente_nome}</div>
                )}
              </div>
            ),
          },
          {
            title: 'Categoria DRE', key: 'cat', width: 380,
            render: (_: any, r: any) => (
              <Select
                value={r.categoria_dre || undefined}
                onChange={(v) => vincular(r.clockify_project_id, r.clockify_project_nome, v || null)}
                placeholder="— sem mapeamento (default: Mão de obra administrativa) —"
                allowClear showSearch optionFilterProp="label"
                size="small" style={{ width: '100%' }}
                loading={salvandoId === r.clockify_project_id}
                options={categoriasDre.map((c: any) => ({
                  label: `${c.categoria} (${c.grupo_dre})`,
                  value: c.categoria,
                }))} />
            ),
          },
          {
            title: 'Grupo DRE', dataIndex: 'grupo_dre', key: 'g', width: 220,
            render: (v: string) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary" style={{ fontSize: 10 }}>—</Text>,
          },
        ] as ColumnsType<any>}
      />
    </Card>
  );
}

// ─── SEÇÃO: Relatório diário Clockify ───────────────────────────
function SecaoRelatorioDiario() {
  const { message } = AntApp.useApp();
  const [cfg, setCfg] = useState<any>(null);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [savCfg, setSavCfg] = useState(false);
  const [disparando, setDisparando] = useState(false);
  const [novoDestinatario, setNovoDestinatario] = useState('');
  // Default: ontem (BRT). Hoje tem dados incompletos durante o expediente.
  const [dataTeste, setDataTeste] = useState<dayjs.Dayjs>(() => dayjs().subtract(1, 'day'));

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_relatorio_config_get' },
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'clockify_relatorio_user_map_listar' },
          { headers: { Authorization: token } }),
      ]);
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setCfg(d1 || { destinatarios: [], ativo: true });
      setUsuarios(d2.usuarios || []);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function salvarCfg(patch: any) {
    setSavCfg(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_relatorio_config_set', ...patch },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSavCfg(false); }
  }

  async function adicionarDestinatario() {
    const email = novoDestinatario.trim().toLowerCase();
    if (!email.includes('@')) { message.warning('Email inválido'); return; }
    const novos = Array.from(new Set([...(cfg?.destinatarios || []), email]));
    await salvarCfg({ destinatarios: novos });
    setNovoDestinatario('');
  }

  async function removerDestinatario(email: string) {
    const novos = (cfg?.destinatarios || []).filter((e: string) => e !== email);
    await salvarCfg({ destinatarios: novos });
  }

  async function toggleAtivoUsuario(email: string, ativo: boolean) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_relatorio_user_map_toggle_ativo', email, ativo },
        { headers: { Authorization: token } });
      setUsuarios(us => us.map(u => u.email === email ? { ...u, ativo } : u));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function dispararAgora() {
    const dataStr = dataTeste.format('YYYY-MM-DD');
    if (!window.confirm(`Enviar relatório do dia ${dataTeste.format('DD/MM/YYYY')} pra ${cfg?.destinatarios?.length || 0} destinatário(s)?`)) return;
    setDisparando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'clockify_relatorio_disparar_agora', data: dataStr },
        { headers: { Authorization: token }, timeout: 60000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.skipped) {
        message.warning('Não enviado: ' + d.motivo);
      } else if (d.enviado) {
        message.success(`Relatório do dia ${dataTeste.format('DD/MM/YYYY')} enviado. ${d.total_pessoas} pessoa(s) · ${d.abaixo_de_6h} < 6h · ${d.sem_horas} sem horas.`);
      } else {
        message.error('Envio falhou: ' + (d.erro_envio || 'erro desconhecido'));
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setDisparando(false); }
  }

  if (loading) return <Card size="small"><Spin /></Card>;

  const destinatarios = cfg?.destinatarios || [];

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>📨 Relatório diário Clockify</Text>}
        extra={
          <Space size={6}>
            <DatePicker size="small" value={dataTeste} onChange={d => d && setDataTeste(d)}
              format="DD/MM/YYYY" disabledDate={d => d.isAfter(dayjs(), 'day')} />
            <Button type="primary" size="small" loading={disparando}
              onClick={dispararAgora}>📨 Enviar teste</Button>
          </Space>
        }>
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 11 }}
          message="Cron automático: 20h BRT diariamente. Pro teste manual o default é ontem (durante o dia o pessoal ainda está preenchendo)." />
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 12 }}>Status: </Text>
          <Tag color={cfg?.ativo ? 'success' : 'default'}>
            {cfg?.ativo ? 'ATIVO (envia)' : 'DESATIVADO (não envia)'}
          </Tag>
          <Button type="link" size="small" disabled={savCfg}
            onClick={() => salvarCfg({ ativo: !cfg?.ativo })}>
            {cfg?.ativo ? 'Desativar' : 'Ativar'}
          </Button>
        </div>

        <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>Destinatários ({destinatarios.length})</Title>
        <Flex gap={6} style={{ marginBottom: 10 }} wrap>
          {destinatarios.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 11 }}>Nenhum email cadastrado.</Text>
          ) : destinatarios.map((email: string) => (
            <Tag key={email} closable onClose={() => removerDestinatario(email)}
              style={{ fontSize: 11 }}>
              {email}
            </Tag>
          ))}
        </Flex>
        <Flex gap={6}>
          <Input placeholder="email@destino.com" size="small" style={{ maxWidth: 280 }}
            value={novoDestinatario}
            onChange={e => setNovoDestinatario(e.target.value)}
            onPressEnter={adicionarDestinatario}
            disabled={savCfg} />
          <Button size="small" onClick={adicionarDestinatario} disabled={savCfg}>+ Adicionar</Button>
        </Flex>
      </Card>

      <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>👥 Profissionais incluídos ({usuarios.filter(u => u.ativo).length} ativos de {usuarios.length})</Text>}>
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 11 }}
          message="Marque/desmarque quem entra no relatório. Útil pra excluir profissionais inativos sem deletar o mapping Clockify." />
        <Table size="small" rowKey="email" dataSource={usuarios}
          pagination={false}
          columns={[
            {
              title: 'Inclui',
              key: 'ativo',
              width: 60,
              render: (_, r: any) => (
                <input type="checkbox" checked={!!r.ativo}
                  onChange={e => toggleAtivoUsuario(r.email, e.target.checked)} />
              ),
            },
            { title: 'Nome', dataIndex: 'clockify_user_name', key: 'n', render: (v: string) => v || <Text type="secondary">—</Text> },
            { title: 'Email', dataIndex: 'email', key: 'e', render: (v: string) => <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>{v}</Text> },
          ] as ColumnsType<any>}
        />
      </Card>
    </Space>
  );
}
